import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { routes } from '@sternum/web/config/routes';
import { useNavigateWithSearch } from '@sternum/web/hooks';
import { appStorage } from '@sternum/web/services/appStorage';
import { store } from '@sternum/web/store';
import { clearReduxStoreAction } from '@sternum/web/store/globalActions';

import { useLogoutApi } from '../useLogoutApi';

interface UseLogoutOptions {
  setLastLocationPath?: boolean;
}

export function useLogout(options: UseLogoutOptions = {}) {
  const { setLastLocationPath = true } = options;

  const navigate = useNavigateWithSearch();
  const queryClient = useQueryClient();
  const { mutateAsync: logoutApi, ...rest } = useLogoutApi();

  const logout = useCallback(async () => {
    try {
      await logoutApi({});
    } catch (e) {
      // we don't need to do anything when this error happens,
      // because the logout api is only used for reporting,
      // if it fails, don't fail the whole logout procedure,
      // it also prevents an infinite loop
      console.error(e);
    }

    appStorage.removeToken();
    appStorage.clearClientSpecificData();
    store.dispatch(clearReduxStoreAction());
    queryClient.clear();

    const currentLocationPath = window.location.href.split(window.location.origin)[1];
    const isLoginPath = routes.auth.login.absolute;
    const isMFAPath = routes.auth.mfa.absolute;

    // Ignore login/mfa paths
    if (currentLocationPath === isLoginPath || currentLocationPath === isMFAPath) return;

    if (setLastLocationPath) {
      appStorage.setLastLocationPath(currentLocationPath);
    }

    navigate(routes.auth.login.absolute);
  }, [logoutApi, navigate, queryClient, setLastLocationPath]);

  return {
    logout,
    ...rest,
  };
}
